import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { CancelReviews } from "@/pages/admin/CancelReviews";

const SpeedSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/speed-settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { setS((await api.put("/admin/speed-settings", s)).data); toast.success("Speed settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const num = (v) => Number(v) || 0;
  return (
    <div className="lux-card space-y-4 p-4 sm:p-6" data-testid="speed-settings">
      <p className="lux-eyebrow">Speeding rule</p>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="space-y-1"><span className="block text-xs text-zinc-400">mph over limit</span>
          <input type="number" data-testid="speed-over-input" className="lux-input !w-28" value={s.over_limit_mph} onChange={(e) => setS({ ...s, over_limit_mph: num(e.target.value) })} /></label>
        <label className="space-y-1"><span className="block text-xs text-zinc-400">Sustained seconds</span>
          <input type="number" data-testid="speed-duration-input" className="lux-input !w-28" value={s.sustained_seconds} onChange={(e) => setS({ ...s, sustained_seconds: num(e.target.value) })} /></label>
      </div>
      <p className="text-xs text-zinc-500">Road-type defaults (mph) — used where OpenStreetMap has no posted limit</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(s.road_defaults).map(([k, v]) => (
          <label key={k} className="space-y-1 text-sm"><span className="block text-xs capitalize text-zinc-400">{k}</span>
            <input type="number" data-testid={`road-default-${k}`} className="lux-input" value={v} onChange={(e) => setS({ ...s, road_defaults: { ...s.road_defaults, [k]: num(e.target.value) } })} /></label>
        ))}
      </div>
      <button data-testid="speed-settings-save" onClick={save} className="btn-gold">Save</button>
    </div>
  );
};

const ALERT_LABEL = {
  customer_hold: "Customer on hold", driver_complaints_72h: "3 complaints in 72 h", driver_arrival_cancels: "Driver cancelled after arriving (2nd this month)",
  driver_termination_pending: "Termination pending", customer_arrival_cancels: "Customer cancelled after arrival (2nd this month)",
};

const Alert = ({ a }) => a.type && a.type !== "speeding_record" ? (
  <div data-testid={`safety-alert-${a.id}`} className="border-t border-white/5 py-3 first:border-0">
    <p className="text-amber-100"><span className="font-semibold text-red-300">{ALERT_LABEL[a.type] || a.type}</span> · {a.customer_name || a.driver_name}{a.driver_code ? ` (${a.driver_code})` : ""}</p>
    <p className="text-xs text-zinc-500">{a.message} · {fmtDate(a.created_at)}</p>
    <ul className="mt-2 space-y-1 text-xs text-zinc-400">{(a.complaints || []).map((c) => <li key={c.id}>{fmtDate(c.created_at)} · {c.category} · {c.author_name}: {c.description}</li>)}</ul>
  </div>
) : (
  <div data-testid={`safety-alert-${a.id}`} className="border-t border-white/5 py-3 first:border-0">
    <p className="text-amber-100"><span className="font-semibold text-red-300">Speeding record</span> · {a.driver_name} ({a.driver_code || "—"}) · {a.month}</p>
    <p className="text-xs text-zinc-500">Flagged {fmtDate(a.created_at)}</p>
    <ul className="mt-2 space-y-1 text-xs text-zinc-400">
      {a.events.map((e) => <li key={e.id}>{fmtDate(e.at)} · {Math.round(e.speed_mph)} mph in a {e.limit_mph} mph zone ({e.limit_source}) · {e.lat.toFixed(4)}, {e.lng.toFixed(4)}</li>)}
    </ul>
  </div>
);

export const SafetyAdmin = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/safety").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6">
      <CancelReviews />
      <div className="lux-card p-4 sm:p-6" data-testid="safety-alerts">
        <p className="lux-eyebrow mb-3">Safety alerts · speeding records and complaint alerts</p>
        {rows && rows.length === 0 && <p className="text-sm text-zinc-500">No safety alerts.</p>}
        {(rows || []).map((a) => <Alert key={a.id} a={a} />)}
      </div>
      <SpeedSettings />
    </div>
  );
};
